// app/src/screens/Script.jsx — the script + render gate (handoff §4), pixel-close. CREATE-2:
// REAL now. Two priced phases, both through the draft:
//   A) GENERATE the script — draftScript({setJobId,cardId}) returns the 30-credit confirm gate;
//      confirm re-sends setJobId+cardId; we watch the job and render script.scenes VERBATIM.
//   B) APPROVE the render — draftApprove({quoteOnly}) → the REAL credit quote → draftApprove(
//      {confirm}) with ONE idempotency key → Generating. Never passes `mode` (the server forces
//      live; the dev rig alone honours mock). A 409 with a resume pointer means the draft already
//      has its render — we follow the pointer.
// The steer box re-scripts (another 30-credit pass); the change-idea chip returns to Ideas.

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, TriangleAlert } from 'lucide-react';
import { navigate } from '@common/router.js';
import { watchJob } from '@common/progress.js';
import { getDraft, draftScript, draftApprove } from '@common/drafts.js';
import { newIdemKey } from '@common/commands.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useFlow } from '../lib/flow.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useToast } from '../lib/toast.jsx';
import { useTopUp } from '../lib/topup.jsx';
import { StepIndicator } from '../components/ui.jsx';
import { resumeRoute } from '../lib/create-nav.js';

const LIVE = ['queued', 'running'];
const SCENE_ACCENTS = ['coral', 'violet', 'mint', 'tangerine'];

export default function Script() {
  const { t } = useI18n();
  const { api, refreshCredits } = useSession();
  const { draftId, pick, clearPick } = useFlow();
  const mobile = useIsMobile();
  const toast = useToast();
  const openTopUp = useTopUp();
  const [draft, setDraft] = useState(null);
  const [watching, setWatching] = useState(false);
  const [scriptGate, setScriptGate] = useState(null); // { quote, pick?, steer? } | { ooc, credits_required, credits_balance }
  const [renderQuote, setRenderQuote] = useState(null);
  const [renderGate, setRenderGate] = useState(null); // { kind:'insufficient_credits'|'refused', ... }
  const [steerText, setSteerText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const scriptKey = useRef(null);
  const approveKey = useRef(null);
  const stopRef = useRef(null);

  const pickFrom = (d) => pick || (d && d.chosen_idea ? { setJobId: d.chosen_idea.job_id, cardId: d.chosen_idea.card_id } : null);
  // CATCHUP F1: a transient pick that names a DIFFERENT card than the draft's recorded
  // chosen_idea = the user came back from "← change idea" and picked another card. The screen
  // must re-script (its own 30-credit gate) — never render the new title over the old scenes,
  // never approve the old card at the full quote.
  const repickedFrom = (d) => !!(pick && d && d.chosen_idea
    && (String(pick.setJobId) !== String(d.chosen_idea.job_id) || String(pick.cardId) !== String(d.chosen_idea.card_id)));

  const refresh = async () => {
    const r = await getDraft(api, draftId);
    if (r.kind === 'ok') { setDraft(r.body); return r.body; }
    setErr(r.error || t('common.error'));
    return null;
  };

  function watchScript(jobId) {
    if (stopRef.current) stopRef.current();
    setWatching(true);
    stopRef.current = watchJob({
      api, jobId,
      onState: async ({ state }) => {
        if (['succeeded', 'failed', 'refused', 'cancelled'].includes(state)) {
          setWatching(false);
          const d = await refresh(); refreshCredits();
          // the pick is now durably recorded on the draft (chosen_idea) — drop the transient
          // one so a later remount reads the recorded choice, not a stale re-pick (F1).
          if (d && d.script && d.script.state === 'succeeded') { clearPick(); quoteRender(); }
        }
      },
    });
  }

  useEffect(() => {
    if (!draftId) { navigate('/'); return; }
    (async () => {
      const d = await refresh();
      if (!d) return;
      const sc = d.script;
      if (sc && LIVE.includes(sc.state)) { watchScript(sc.job_id); return; }
      // CATCHUP F1: consult the NEW pick BEFORE the succeeded early-return. A re-pick (a card
      // that differs from the recorded chosen_idea) re-scripts through its own honest 30-credit
      // gate; the succeeded-script return only wins when the pick still names the recorded card.
      if (repickedFrom(d)) { runScript({ pick }); return; }
      if (sc && sc.state === 'succeeded') { quoteRender(); return; }
      // phase A — no finished script yet. Resolve the chosen card, then surface the 30-credit
      // gate (a non-confirm draftScript prices without spending; scripting is never free).
      const p = pickFrom(d);
      if (!p) { navigate('/create/ideas'); return; }
      runScript({ pick: p });
    })();
    return () => { if (stopRef.current) stopRef.current(); };
  }, [draftId]); // eslint-disable-line react-hooks/exhaustive-deps

  // phase A: one script pass (generate or steer). confirm=false prices (needs_confirm, no spend);
  // confirm=true re-sends the SAME setJobId+cardId (+steer) through the gate and enqueues.
  async function runScript({ pick: p = null, steer = null, confirm = false } = {}) {
    if (busy || !draftId) return;
    setBusy(true); setErr(null);
    if (!confirm) scriptKey.current = newIdemKey();
    const body = { steer: steer || null, confirm, idempotencyKey: scriptKey.current };
    if (p) { body.setJobId = p.setJobId; body.cardId = p.cardId; }
    const r = await draftScript(api, draftId, body);
    setBusy(false);
    if (r.kind === 'ok') {
      setScriptGate(null); setSteerText('');
      // CATCHUP F3: close the one-RTT window between the steer enqueue and the watch flip where
      // the approve bar was still up on the STALE quote — a click there rendered the OLD genblocks
      // and stranded the freshly-paid steer (30 cr). Hide phase B (watching) and drop the quote
      // (disables approve) BEFORE the awaits; the server-side 409 is the real fence, this removes
      // the reachable UI path.
      setWatching(true); setRenderQuote(null);
      const d = await refresh(); refreshCredits();
      if (d && d.script && LIVE.includes(d.script.state)) watchScript(d.script.job_id);
      // the job can ALREADY be terminal at refresh (a confirm that deduped to an
      // earlier-succeeded pass, or a very fast pass): re-quote directly so approve isn't stranded
      // disabled with a null quote (no watchScript would ever fire to unstick it).
      else { setWatching(false); if (d && d.script && d.script.state === 'succeeded') { clearPick(); quoteRender(); } }
    } else if (r.kind === 'needs_confirm') {
      setScriptGate({ quote: r.quote, pick: p, steer });
    } else if (r.kind === 'insufficient_credits') {
      setScriptGate({ ooc: true, credits_required: r.credits_required, credits_balance: r.credits_balance });
    } else {
      setErr(r.error || t('common.error'));
    }
  }

  // phase B: the render quote (never a row) and the approve (real credits → Generating).
  async function quoteRender() {
    const r = await draftApprove(api, draftId, { quoteOnly: true });
    if (r.kind === 'ok') { setRenderQuote(r.body.quote); setRenderGate(null); }
    else if (r.kind === 'insufficient_credits' || r.kind === 'refused') setRenderGate(r);
    else if (r.kind === 'error' && r.body && r.body.resume) navigate(resumeRoute(r.body.resume));
    else setErr(r.error || t('common.error'));
  }
  async function approve() {
    if (busy) return; setBusy(true); setErr(null);
    if (!approveKey.current) approveKey.current = newIdemKey(); // minted ONCE; retries reuse it
    const r = await draftApprove(api, draftId, { confirm: true, idempotencyKey: approveKey.current });
    setBusy(false);
    if (r.kind === 'ok') { refreshCredits(); navigate(`/create/generating/${encodeURIComponent(r.body.job_id)}`); }
    else if (r.kind === 'needs_confirm') setRenderQuote(r.quote);
    else if (r.kind === 'insufficient_credits') setRenderGate(r);
    else if (r.kind === 'error' && r.body && r.body.resume) navigate(resumeRoute(r.body.resume)); // already has its render
    else setErr(r.error || t('common.error'));
  }

  if (err && !draft) return <div className="view"><p className="muted" style={{ color: 'var(--coral)' }}>{err}</p></div>;
  if (!draft) return <div className="view"><p className="muted">{t('common.loading')}</p></div>;

  const sc = draft.script;
  const scenes = (sc && sc.scenes) || [];
  // while a re-pick is pending (F1) the OLD script's scenes + approve bar must NOT show — the
  // screen is the new card's 30-credit generate gate (phase A) until its script exists.
  const repicking = repickedFrom(draft);
  const hasScript = !repicking && !!(sc && sc.state === 'succeeded' && scenes.length);
  const aspect = (draft.settings && draft.settings.aspect) || '9:16';
  const chosenCard = (() => {
    const p = pickFrom(draft);
    if (p) { const set = (draft.idea_sets || []).find((s) => s.job_id === p.setJobId); const c = set && set.set && (set.set.cards || []).find((x) => x.id === p.cardId); if (c) return c; }
    return (draft.chosen_idea && draft.chosen_idea.snapshot) || null;
  })();
  const title = (chosenCard && chosenCard.hook) || (sc && sc.genblocks && sc.genblocks.project && sc.genblocks.project.title) || t('script.title');
  const renderFailed = draft.approve_job && draft.approve_job.state === 'failed';

  const statusChips = [
    { c: 'var(--coral)', v: t('settings.lengthValue') },
    { c: 'var(--violet)', v: aspect },
    { c: 'var(--mint)', v: t('settings.languageValue') },
  ];

  const creditFace = renderQuote && renderQuote.credits;
  const credited = creditFace && creditFace.credits > 0;
  const seconds = renderQuote ? Math.round(renderQuote.footage_seconds || renderQuote.container_seconds || 0) : null;
  const ooc = renderGate && renderGate.kind === 'insufficient_credits';

  return (
    <div className="view" style={{ maxInlineSize: 780 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <StepIndicator current="script" size={mobile ? 'sm' : 'lg'} />
        {!mobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {statusChips.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: 'var(--surface)', border: '1px solid var(--border)', font: '600 12px var(--font-body)' }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: s.c }} />{s.v}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 4px', flexWrap: 'wrap' }}>
        <h2 className="h-section" style={{ margin: 0 }}>{title}</h2>
        <button className="q-btn" onClick={() => navigate('/create/ideas')} style={{ font: '600 11.5px var(--font-mono)', color: 'var(--violet)', background: 'var(--violet-tint)', borderRadius: 999, padding: '5px 11px', border: 'none' }}>← {t('script.changeIdea')}</button>
      </div>
      <p className="muted" style={{ margin: '0 0 20px', fontSize: mobile ? 12.5 : 14, lineHeight: 1.5 }}>{t('script.subcopy')}</p>

      {renderFailed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--coral-tint)', borderRadius: 12, padding: '11px 14px', marginBottom: 16 }}>
          <TriangleAlert size={16} color="var(--coral)" strokeWidth={2.2} />
          <span style={{ font: '500 12.5px var(--font-body)', color: 'var(--coral-ink, var(--coral))' }}>{t('script.renderFailedNote')}</span>
        </div>
      )}

      {/* PHASE A — writing / generate gate */}
      {watching && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 999, background: 'var(--coral)', animation: 'pulseDot 1.3s ease-in-out infinite' }} />
          <p className="muted" style={{ marginTop: 14, font: '600 14px var(--font-body)' }}>{t('script.writing')}</p>
        </div>
      )}

      {!watching && !hasScript && scriptGate && (
        <div className="q-card" style={{ maxWidth: 520, margin: '10px auto', padding: 20, borderRadius: 16, border: '1px solid var(--coral-line)', textAlign: 'center' }}>
          {scriptGate.ooc ? (
            <>
              <h4 className="h-card" style={{ margin: '0 0 6px' }}>{t('ideas.oocTitle')}</h4>
              <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('script.needsBalance', { required: scriptGate.credits_required, balance: scriptGate.credits_balance })}</p>
              <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '12px 22px', borderRadius: 12, font: '700 13.5px var(--font-body)' }}>{t('script.outOfCredits')}</button>
            </>
          ) : (
            <>
              <h4 className="h-card" style={{ margin: '0 0 6px' }}>{t('script.generateTitle')}</h4>
              <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('script.generatePrice', { credits: (scriptGate.quote && scriptGate.quote.credits && scriptGate.quote.credits.credits) || '—' })}</p>
              <button className="q-btn press" disabled={busy} onClick={() => runScript({ pick: scriptGate.pick, steer: scriptGate.steer, confirm: true })} style={{ background: 'var(--coral)', color: '#fff', padding: '13px 26px', borderRadius: 12, font: '700 14px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)', opacity: busy ? 0.6 : 1 }}>{t('script.generateCta')} →</button>
            </>
          )}
        </div>
      )}

      {/* PHASE B — the scenes + steer + approve */}
      {hasScript && !watching && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: mobile ? 10 : 12 }}>
            {scenes.map((s, i) => {
              const acc = SCENE_ACCENTS[i % 4];
              return (
                <div key={s.id || i} className="scene-row qcard" style={mobile ? { borderRadius: 15, padding: 13, gap: 12 } : undefined}>
                  <div className="scene-num" style={{ width: mobile ? 34 : 44, height: mobile ? 34 : 44, borderRadius: mobile ? 10 : 12, background: `var(--${acc}-tint)`, color: `var(--${acc})`, fontSize: mobile ? 13 : 15 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div className="mono" style={{ font: `600 ${mobile ? 9.5 : 11}px var(--font-mono)`, color: 'var(--text-faint)', marginBottom: 4, textTransform: mobile ? 'uppercase' : 'none' }}>{t('script.visual').toUpperCase()} — {s.visual}</div>
                    {s.line && <div style={{ font: `500 ${mobile ? 14 : 16}px/1.4 var(--font-body)` }}>“{s.line}”</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* steer input — another 30-credit script pass */}
          <form onSubmit={(e) => { e.preventDefault(); if (steerText.trim()) runScript({ steer: steerText.trim() }); }} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: mobile ? 14 : 16, padding: mobile ? '9px 9px 9px 14px' : '10px 10px 10px 16px', marginTop: mobile ? 14 : 16 }}>
            <Sparkles size={mobile ? 15 : 17} color="var(--coral)" strokeWidth={2} />
            <input value={steerText} onChange={(e) => setSteerText(e.target.value)} placeholder={t('script.steerPlaceholder')} style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', font: `500 ${mobile ? 13 : 14}px var(--font-body)`, color: 'var(--ink)' }} />
            <button type="submit" className="q-btn press" disabled={busy || !steerText.trim()} style={{ background: 'var(--fill-ghost)', color: 'var(--ink)', padding: mobile ? '8px 13px' : '10px 17px', borderRadius: mobile ? 10 : 12, font: `700 ${mobile ? 11.5 : 12.5}px var(--font-body)`, opacity: (busy || !steerText.trim()) ? 0.5 : 1 }}>{t('script.send')}</button>
          </form>

          {/* steer confirm gate (30 credits) */}
          {scriptGate && (
            <div className="q-card" style={{ marginTop: 12, padding: 14, borderRadius: 14, border: '1px solid var(--coral-line)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ flex: 1, font: '500 13px var(--font-body)', color: 'var(--text-muted)' }}>{scriptGate.ooc ? t('script.needsBalance', { required: scriptGate.credits_required, balance: scriptGate.credits_balance }) : t('script.steerPrice', { credits: (scriptGate.quote && scriptGate.quote.credits && scriptGate.quote.credits.credits) || '—' })}</span>
              {scriptGate.ooc ? (
                <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '10px 18px', borderRadius: 12, font: '700 13px var(--font-body)' }}>{t('script.outOfCredits')}</button>
              ) : (
                <>
                  <button className="q-btn press btn-ghost" onClick={() => setScriptGate(null)}>{t('common.cancel')}</button>
                  <button className="q-btn press" disabled={busy} onClick={() => runScript({ steer: scriptGate.steer, confirm: true })} style={{ background: 'var(--coral)', color: '#fff', padding: '10px 18px', borderRadius: 12, font: '700 13px var(--font-body)', opacity: busy ? 0.6 : 1 }}>{t('ideas.confirmSpend')}</button>
                </>
              )}
            </div>
          )}

          {/* approve bar (the render — real credits) */}
          {mobile ? (
            <>
              <button className="q-btn press btn-primary" disabled={ooc ? false : (!renderQuote || busy)} onClick={ooc ? openTopUp : approve} style={{ marginTop: 12 }}>{ooc ? t('script.outOfCredits') : <>{t('script.approve')} →</>}</button>
              <div style={{ textAlign: 'center', font: '500 11px var(--font-mono)', color: ooc ? 'var(--coral)' : 'var(--text-faint)', marginTop: 9 }}>
                {ooc
                  ? t('script.needsBalance', { required: renderGate.credits_required, balance: renderGate.credits_balance })
                  : seconds == null ? t('common.loading')
                    : credited ? t('script.priceLine', { seconds, time: t('script.renderTime'), credits: creditFace.credits })
                      : t('script.priceFree', { seconds, time: t('script.renderTime') })}
              </div>
            </>
          ) : (
            <div className="q-card" style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16, padding: '13px 16px', boxShadow: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, font: '500 12.5px var(--font-body)', color: 'var(--text-muted)' }}>
                {seconds != null ? (
                  <>
                    <span>{seconds}s</span><span style={{ color: 'var(--border-strong)' }}>·</span>
                    <span>{t('script.renderTime')}</span><span style={{ color: 'var(--border-strong)' }}>·</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {credited && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--tangerine)' }} />}
                      {credited ? t('script.credits', { credits: creditFace.credits }) : t('script.free')}
                    </span>
                  </>
                ) : <span>{t('common.loading')}</span>}
              </div>
              {ooc ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="muted" style={{ fontSize: 12 }}>{t('script.needsBalance', { required: renderGate.credits_required, balance: renderGate.credits_balance })}</span>
                  <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '13px 22px', borderRadius: 12, font: '700 14px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)' }}>{t('script.outOfCredits')}</button>
                </div>
              ) : (
                <button className="q-btn press" disabled={!renderQuote || busy} onClick={approve} style={{ background: 'var(--coral)', color: '#fff', padding: '13px 24px', borderRadius: 12, font: '700 14px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)', opacity: (!renderQuote || busy) ? 0.6 : 1 }}>{t('script.approve')} →</button>
              )}
            </div>
          )}
          {creditFace && creditFace.credits_hold > creditFace.credits && !ooc && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>{t('script.holdNote', { hold: creditFace.credits_hold })}</p>
          )}
          {renderGate && renderGate.kind === 'refused' && <p className="muted" style={{ color: 'var(--coral)' }}>{renderGate.error}</p>}
        </>
      )}

      {err && draft && <p className="muted" style={{ color: 'var(--coral)', marginTop: 12 }}>{err}</p>}
    </div>
  );
}
